/**
 * Carbon offset plan sync. CARBON-PLAN-SYNC-2026-10
 *
 * The carbon offset is added to the cart once, on the selling plan of the
 * product being added at that moment, and nothing updated it afterwards. If
 * the shopper then switched that product to one-time, removed it, or kept
 * products on a plan the carbon offset cannot share (6 months, LE refill),
 * the carbon line still checked out on "Deliver every 3 months" and Loop made
 * it a subscription of its own: $0.99 every cycle with nothing to offset.
 *
 * Rule: the carbon offset only recurs alongside products. After every cart
 * write this puts the carbon line on the carbon plan matching the interval of
 * the subscribed products, or makes it one-time when none match. It only ever
 * touches the carbon line, and never adds or removes it.
 */
(function (root, doc) {
  'use strict';

  var CARBON_VARIANT_ID = 42280675836083;
  var CARBON_HANDLE = 'carbon-offset-contribution';

  // "Deliver every 3 months", "Deliver every 1 months  ", "Deliver every month"
  // -> months. Anything else (e.g. "about every 3 months", weeks) -> null, so
  // the carbon line goes one-time rather than onto a plan Loop won't merge.
  function planMonths(name) {
    var m = /^deliver every(?: (\d+))? months?$/.exec(
      String(name || '').toLowerCase().replace(/\s+/g, ' ').trim()
    );
    return m ? Number(m[1] || 1) : null;
  }

  function carbonPlansByMonths(product) {
    var out = {};
    ((product && product.selling_plan_groups) || []).forEach(function (g) {
      (g.selling_plans || []).forEach(function (p) {
        var months = planMonths(p.name);
        if (months && !out[months]) out[months] = p.id;
      });
    });
    return out;
  }

  // Selling plan id the carbon line should have (null = one-time), or
  // undefined when the cart has no carbon line or nothing else to go with it.
  function desiredCarbonPlan(items, plansByMonths) {
    items = items || [];
    var carbon = items.filter(function (l) { return Number(l.variant_id) === CARBON_VARIANT_ID; });
    var others = items.filter(function (l) { return Number(l.variant_id) !== CARBON_VARIANT_ID; });
    if (!carbon.length || !others.length) return undefined;
    // Most common matching interval; ties go to the most recently added line
    // (cart.js lists newest first), like the drawer's own plan pick.
    var counts = {};
    var order = [];
    others.forEach(function (l) {
      var sp = l.selling_plan_allocation && l.selling_plan_allocation.selling_plan;
      var months = sp && planMonths(sp.name);
      if (!months || !plansByMonths[months]) return;
      if (!counts[months]) { counts[months] = 0; order.push(months); }
      counts[months] += Number(l.quantity) || 1;
    });
    var best = null;
    order.forEach(function (m) { if (best === null || counts[m] > counts[best]) best = m; });
    return best === null ? null : plansByMonths[best];
  }

  function carbonFixes(items, plansByMonths) {
    var want = desiredCarbonPlan(items, plansByMonths);
    if (want === undefined) return [];
    return (items || [])
      .filter(function (l) { return Number(l.variant_id) === CARBON_VARIANT_ID; })
      .filter(function (l) {
        var sp = l.selling_plan_allocation && l.selling_plan_allocation.selling_plan;
        return (sp ? sp.id : null) !== want;
      })
      .map(function (l) { return { id: l.key, quantity: l.quantity, selling_plan: want }; });
  }

  var plansPromise = null;
  function carbonPlans(doFetch) {
    if (!plansPromise) {
      plansPromise = doFetch('/products/' + CARBON_HANDLE + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(carbonPlansByMonths)
        .catch(function () { plansPromise = null; return {}; });
    }
    return plansPromise;
  }

  var busy = false;
  var pending = false;
  function sweep() {
    var doFetch = root.__carbonSyncOrigFetch || root.fetch;
    if (typeof doFetch !== 'function') return;
    // A cart change while a sweep is in flight must not be dropped: that
    // sweep read the older cart. Remember it and run again when done.
    if (busy) { pending = true; return; }
    busy = true;
    pending = false;
    Promise.all([
      carbonPlans(doFetch),
      doFetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); })
    ])
      .then(function (res) {
        // Plans didn't load: leave the cart alone rather than guess.
        if (!Object.keys(res[0] || {}).length) return null;
        var fixes = carbonFixes(res[1].items, res[0]);
        // One line per change call; the line key changes with the plan, so
        // re-read the cart (pending) if there were several carbon lines.
        if (!fixes.length) return null;
        if (fixes.length > 1) pending = true;
        return doFetch('/cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(fixes[0])
        }).then(function () {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'carbon-plan-sync' });
          }
        });
      })
      .catch(function (e) { if (root.console) root.console.error('[Carbon plan sync]', e); })
      .then(function () {
        busy = false;
        if (pending) sweep();
      });
  }

  root.TecCarbonPlanSync = {
    planMonths: planMonths,
    carbonPlansByMonths: carbonPlansByMonths,
    desiredCarbonPlan: desiredCarbonPlan,
    carbonFixes: carbonFixes,
    sweep: sweep
  };

  if (!doc) return;   // node tests: pure half only

  function init() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (ev) {
        if (ev && ev.source === 'carbon-plan-sync') return;
        setTimeout(sweep, 0);
      });
    }
    // Our own writes go through the unpatched fetch so they don't re-trigger.
    var CART_WRITE_RE = /\/cart\/(add|change|update)(\.js)?(\?|$)/;
    var orig = root.fetch;
    if (orig && !orig.__carbonPlanSyncPatched) {
      root.__carbonSyncOrigFetch = orig;
      root.fetch = function () {
        var url = typeof arguments[0] === 'string' ? arguments[0] : (arguments[0] && arguments[0].url) || '';
        var p = orig.apply(this, arguments);
        if (CART_WRITE_RE.test(url)) p.then(function () { setTimeout(sweep, 60); }, function () {});
        return p;
      };
      root.fetch.__carbonPlanSyncPatched = true;
    }
    sweep();
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init);
  else init();
})(typeof window !== 'undefined' ? window : this, typeof document !== 'undefined' ? document : undefined);
